import { Mesh, MeshPhysicalMaterial, MeshStandardMaterial } from "three";

import { CAR_MATERIAL_LIBRARY } from "./carMaterials.js";

/**
 * Applies the material library onto a loaded car scene, matching by
 * material name. Values are written *onto* the model's own materials
 * instead of swapping in freshly built ones, because this model's
 * materials carry texture maps that matter (tyre tread/normal map, grille
 * mesh, head/tail/indicator lens textures) — replacing them wholesale
 * would throw all of that away. Anything without a library entry is left
 * exactly as authored.
 */

/** Physical-only features. A material needing one of these has to be a
 * `MeshPhysicalMaterial` — setting `transmission` on a plain standard
 * material silently does nothing. */
function needsPhysical(spec) {
  return (
    spec.transmission !== undefined ||
    spec.clearcoat !== undefined ||
    spec.clearcoatRoughness !== undefined ||
    spec.ior !== undefined ||
    spec.thickness !== undefined
  );
}

/** Promotes a standard material to a physical one, carrying every texture
 * map and surface value across. */
function toPhysical(source) {
  const physical = new MeshPhysicalMaterial();
  MeshStandardMaterial.prototype.copy.call(physical, source);
  physical.name = source.name;
  return physical;
}

function applySpec(material, spec) {
  if (spec.color !== undefined) material.color.set(spec.color);
  if (spec.metalness !== undefined) material.metalness = spec.metalness;
  if (spec.roughness !== undefined) material.roughness = spec.roughness;
  if (spec.envMapIntensity !== undefined) material.envMapIntensity = spec.envMapIntensity;
  if (spec.transparent !== undefined) material.transparent = spec.transparent;
  if (spec.opacity !== undefined) material.opacity = spec.opacity;

  if (spec.emissive !== undefined) {
    material.emissive.set(spec.emissive);
    material.emissiveIntensity = spec.emissiveIntensity ?? 1;
    // A model can carry an emissive *texture* that would otherwise mask the
    // colour set here; the lens materials rely on the flat tint instead.
    material.emissiveMap = null;
  }

  if (material instanceof MeshPhysicalMaterial) {
    if (spec.clearcoat !== undefined) material.clearcoat = spec.clearcoat;
    if (spec.clearcoatRoughness !== undefined) material.clearcoatRoughness = spec.clearcoatRoughness;
    if (spec.transmission !== undefined) material.transmission = spec.transmission;
    if (spec.thickness !== undefined) material.thickness = spec.thickness;
    if (spec.ior !== undefined) material.ior = spec.ior;
    if (spec.specularIntensity !== undefined) material.specularIntensity = spec.specularIntensity;
    if (spec.specularColor !== undefined) material.specularColor.set(spec.specularColor);
  }

  material.needsUpdate = true;
}

/**
 * Walks the scene and retunes every named material in the library.
 * Materials are shared between primitives, so the same one is reached many
 * times over a walk; `seen`/`promoted` keep the work to once each and keep
 * the promoted-material swap stable across meshes that share a material.
 */
export function applyCarMaterials(scene) {
  const promoted = new Map();
  const seen = new Set();

  const retune = (material) => {
    const spec = material.name ? CAR_MATERIAL_LIBRARY[material.name] : undefined;
    if (!spec || !(material instanceof MeshStandardMaterial)) return material;

    if (needsPhysical(spec) && !(material instanceof MeshPhysicalMaterial)) {
      const existing = promoted.get(material);
      if (existing) return existing;
      const physical = toPhysical(material);
      applySpec(physical, spec);
      promoted.set(material, physical);
      return physical;
    }

    if (!seen.has(material)) {
      seen.add(material);
      applySpec(material, spec);
    }
    return material;
  };

  scene.traverse((child) => {
    if (!(child instanceof Mesh)) return;

    child.material = Array.isArray(child.material) ? child.material.map(retune) : retune(child.material);

    child.castShadow = true;
    child.receiveShadow = true;
  });
}
